import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

// Modular Admin Components
import AdminHeader from "./layout/AdminHeader";
import AdminStats from "./components/AdminStats";
import AdminTabs from "./components/AdminTabs";
import StudentDirectory from "./components/StudentDirectory";
import DuesManagement from "./components/DuesManagement";
import DepartmentManagement from "./components/DepartmentManagement";
import AddStudentForm from "./components/AddStudentForm";
import SystemSettings from "./components/SystemSettings";
import SystemLogs from "./components/SystemLogs";
import StudentDetailsModal from "./components/StudentDetailsModal";
import { exportStudentsToCSV } from "./utils/exportCsv";
import "./AdminPanel.css";

const AdminPanel = () => {
  const { token, setIsuserloggedin, setToken } = useAuth();
  const navigate = useNavigate();

  // Tab & student list state
  const [activeTab, setActiveTab] = useState("students");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination & Server-side stats
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    currentPage: 1,
    limit: 10,
  });
  const [stats, setStats] = useState({
    totalCount: 0,
    paidCount: 0,
    inPersonCount: 0,
    courierCount: 0,
  });

  // Search & Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPayment, setFilterPayment] = useState("all");
  const [filterAttendance, setFilterAttendance] = useState("all");

  // Selected student for profile modal
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Due management state
  const [dueRegNo, setDueRegNo] = useState("");
  const [dueAmount, setDueAmount] = useState("");
  const [dueDepartment, setDueDepartment] = useState("Finance");
  const [dueDetails, setDueDetails] = useState("");
  const [studentDues, setStudentDues] = useState([]);
  const [searchedStudent, setSearchedStudent] = useState(null);
  const [dueLoading, setDueLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState({ text: "", type: "" });

  // Add new student state (clean empty form matching Dummy.xlsx master columns)
  const initialStudentState = {
    reg_no: "",
    student_name: "",
    email: "",
    gender: "M",
    faculty: "",
    school: "",
    programme: "",
    specialization: "",
    batch: "",
    credits: "",
    cgpa: "",
  };
  const [newStudent, setNewStudent] = useState(initialStudentState);

  // Fetch students from backend with pagination & filters
  const fetchStudents = async (params = {}) => {
    try {
      setLoading(true);
      const page = params.page !== undefined ? params.page : pagination.currentPage || 1;
      const search = params.search !== undefined ? params.search : searchTerm;
      const payment = params.payment !== undefined ? params.payment : filterPayment;
      const attendance = params.attendance !== undefined ? params.attendance : filterAttendance;
      const limit = params.limit !== undefined ? params.limit : 10;

      const queryParams = new URLSearchParams();
      queryParams.append("page", page);
      queryParams.append("limit", limit);
      if (search && search.trim()) queryParams.append("search", search.trim());
      if (payment && payment !== "all") queryParams.append("payment", payment);
      if (attendance && attendance !== "all") queryParams.append("attendance", attendance);

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/student/get-all-students?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (res.data) {
        setStudents(res.data.data || []);
        if (res.data.pagination) {
          setPagination(res.data.pagination);
        }
        if (res.data.stats) {
          setStats(res.data.stats);
        }
      }
    } catch (err) {
      console.error("Error fetching students:", err);
      if (err?.response?.status === 401) {
        setIsuserloggedin(false);
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const verifyAdmin = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_BASE}/auth/getUser`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.data?.data || res.data.data.role !== "admin") {
          localStorage.removeItem("login");
          setIsuserloggedin(false);
          setToken(null);
          navigate("/admin/login");
          return;
        }
        fetchStudents({ page: 1 });
      } catch (err) {
        localStorage.removeItem("login");
        setIsuserloggedin(false);
        setToken(null);
        navigate("/admin/login");
      }
    };

    if (!token) {
      navigate("/admin/login");
    } else {
      verifyAdmin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("login");
    setIsuserloggedin(false);
    setToken(null);
    navigate("/admin/login");
  };

  // Search handler (triggered on Search button click or submit)
  const handleSearch = (newSearch) => {
    setSearchTerm(newSearch);
    fetchStudents({ page: 1, search: newSearch });
  };

  // Filter handlers
  const handlePaymentFilterChange = (newPayment) => {
    setFilterPayment(newPayment);
    fetchStudents({ page: 1, payment: newPayment });
  };

  const handleAttendanceFilterChange = (newAttendance) => {
    setFilterAttendance(newAttendance);
    fetchStudents({ page: 1, attendance: newAttendance });
  };

  // Page navigation
  const handlePageChange = (newPage) => {
    fetchStudents({ page: newPage });
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterPayment("all");
    setFilterAttendance("all");
    fetchStudents({ page: 1, search: "", payment: "all", attendance: "all" });
  };

  // Export CSV (fetches all matching records from backend)
  const handleExportCSV = async () => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("limit", "all");
      if (searchTerm && searchTerm.trim()) queryParams.append("search", searchTerm.trim());
      if (filterPayment && filterPayment !== "all") queryParams.append("payment", filterPayment);
      if (filterAttendance && filterAttendance !== "all") queryParams.append("attendance", filterAttendance);

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/student/get-all-students?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.data && res.data.data) {
        exportStudentsToCSV(res.data.data);
      }
    } catch (err) {
      console.error("Failed to export students CSV:", err);
    }
  };

  // Toggle Payment status
  const handleTogglePayment = async (student) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE}/student/update-student-payment-status/${student.reg_no}`,
        {
          paymentId: student.is_paid ? "" : `MANUAL_ADMIN_${Date.now()}`,
          day: student.day || "01-11-2026",
          companions: student.companions || "0",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActionMessage({
        text: `Updated payment status for ${student.student_name}`,
        type: "success",
      });
      fetchStudents({ page: pagination.currentPage });
    } catch (err) {
      setActionMessage({
        text: err?.response?.data?.message || "Failed to update payment status",
        type: "danger",
      });
    }
  };

  // Delete student
  const handleDeleteStudent = async (reg_no) => {
    if (
      !window.confirm(
        `Are you sure you want to delete student with Reg No: ${reg_no}?`
      )
    ) {
      return;
    }
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE}/student/delete-student-by-reg-no/${reg_no}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActionMessage({
        text: `Student ${reg_no} removed successfully`,
        type: "success",
      });
      fetchStudents({ page: pagination.currentPage });
    } catch (err) {
      setActionMessage({
        text: err?.response?.data?.message || "Failed to delete student",
        type: "danger",
      });
    }
  };

  // Regenerate and Resend Password to Student
  const [regenLoadingId, setRegenLoadingId] = useState(null);

  const handleRegeneratePassword = async (student) => {
    if (!student || !student._id) return;
    const confirmMsg = `Are you sure you want to regenerate and email a new password to ${student.student_name || student.reg_no} (${student.email || "No email"})?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setRegenLoadingId(student._id);
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/student/regenerate-password/${student._id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setActionMessage({
        text: res.data?.message || `New password generated and sent to ${student.email}!`,
        type: "success",
      });
      fetchStudents({ page: pagination.currentPage });
    } catch (err) {
      setActionMessage({
        text:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Failed to regenerate password",
        type: "danger",
      });
    } finally {
      setRegenLoadingId(null);
    }
  };

  // Handler when student record is edited and updated from modal
  const handleStudentUpdated = (updatedStudent) => {
    setSelectedStudent(updatedStudent);
    fetchStudents({ page: pagination.currentPage });
    setActionMessage({
      text: `Student details for ${updatedStudent.student_name} (${updatedStudent.reg_no}) updated successfully!`,
      type: "success",
    });
  };

  // Search dues for a student
  const handleSearchDues = async (e) => {
    if (e) e.preventDefault();
    if (!dueRegNo) return;
    try {
      setDueLoading(true);
      const studentRes = await axios.get(
        `${process.env.REACT_APP_API_BASE}/student/get-specific-student/${dueRegNo}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSearchedStudent(studentRes.data.student);

      const duesRes = await axios.get(
        `${process.env.REACT_APP_API_BASE}/due/get-student-dept-dues/${dueRegNo}`
      );
      setStudentDues(duesRes.data.data || []);
    } catch (err) {
      setSearchedStudent(null);
      setStudentDues([]);
      setActionMessage({
        text: "No student found with registration number " + dueRegNo,
        type: "danger",
      });
    } finally {
      setDueLoading(false);
    }
  };

  // Create new Due
  const handleCreateDue = async (e) => {
    e.preventDefault();
    if (!dueRegNo || !dueAmount) return;
    try {
      setDueLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/due/create-student-dept-due`,
        {
          reg_no: dueRegNo,
          department: dueDepartment,
          amount_due: Number(dueAmount),
          details: dueDetails || "Outstanding due",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActionMessage({
        text: `Due of ₹${dueAmount} added for ${dueRegNo}`,
        type: "success",
      });
      setDueAmount("");
      setDueDetails("");
      handleSearchDues();
    } catch (err) {
      setActionMessage({
        text: err?.response?.data?.message || "Failed to add due",
        type: "danger",
      });
    } finally {
      setDueLoading(false);
    }
  };

  // Clear a Due
  const handleClearDue = async (due) => {
    const dueId = typeof due === "object" && due !== null ? due._id : due;
    const amount = due?.amount_due ? `₹${due.amount_due}` : "this penalty";
    const dept = due?.department ? `from ${due.department}` : "";

    if (
      !window.confirm(
        `Are you sure you want to mark ${amount} ${dept} as Cleared?`
      )
    ) {
      return;
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE}/due/clear-student-due/${dueId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setActionMessage({
        text: `Penalty of ${amount} ${dept} marked as Cleared successfully!`,
        type: "success",
      });
      handleSearchDues();
    } catch (err) {
      setActionMessage({
        text: err?.response?.data?.message || "Failed to clear due",
        type: "danger",
      });
    }
  };

  // Create New Student
  const handleAddStudentSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/student/create-student`,
        newStudent
      );
      setActionMessage({
        text: `Student ${newStudent.student_name} (${newStudent.reg_no}) registered successfully!`,
        type: "success",
      });
      setNewStudent(initialStudentState);
      setActiveTab("students");
      fetchStudents({ page: 1 });
    } catch (err) {
      setActionMessage({
        text: err?.response?.data?.message || "Failed to register new student",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-shell">
      {/* Top Navbar */}
      <AdminHeader onLogout={handleLogout} />

      {/* Main Content */}
      <main className="admin-main-container">
        {/* Flash Alerts */}
        {actionMessage.text && (
          <div
            style={{
              backgroundColor:
                actionMessage.type === "success" ? "#ecfdf5" : "#fef2f2",
              border:
                actionMessage.type === "success"
                  ? "1px solid #6ee7b7"
                  : "1px solid #fca5a5",
              color: actionMessage.type === "success" ? "#065f46" : "#991b1b",
              padding: "12px 18px",
              borderRadius: "8px",
              marginBottom: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{actionMessage.text}</span>
            <button
              onClick={() => setActionMessage({ text: "", type: "" })}
              style={{
                background: "none",
                border: "none",
                fontSize: "16px",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Stats Summary Cards */}
        <AdminStats
          totalCount={stats.totalCount}
          registeredCount={stats.registeredCount}
          notRegisteredCount={stats.notRegisteredCount}
          clearedCount={stats.clearedCount}
          pendingDuesCount={stats.pendingDuesCount}
          paidCount={stats.paidCount}
          inPersonCount={stats.inPersonCount}
          courierCount={stats.courierCount}
        />

        {/* Tab Navigation */}
        <AdminTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          studentCount={pagination.total}
        />

        {/* TAB 1: ALL STUDENTS */}
        {activeTab === "students" && (
          <StudentDirectory
            students={students}
            loading={loading}
            pagination={pagination}
            searchTerm={searchTerm}
            filterPayment={filterPayment}
            filterAttendance={filterAttendance}
            onSearch={handleSearch}
            onPaymentFilterChange={handlePaymentFilterChange}
            onAttendanceFilterChange={handleAttendanceFilterChange}
            onPageChange={handlePageChange}
            onResetFilters={handleResetFilters}
            onExportCSV={handleExportCSV}
            onViewStudent={(s) => {
              setSelectedStudent(s);
              setShowModal(true);
            }}
            onTogglePayment={handleTogglePayment}
            onDeleteStudent={handleDeleteStudent}
            onRegeneratePassword={handleRegeneratePassword}
            regenLoadingId={regenLoadingId}
          />
        )}

        {/* TAB 2: DUES MANAGEMENT */}
        {activeTab === "dues" && (
          <DuesManagement
            dueRegNo={dueRegNo}
            setDueRegNo={setDueRegNo}
            dueDepartment={dueDepartment}
            setDueDepartment={setDueDepartment}
            dueAmount={dueAmount}
            setDueAmount={setDueAmount}
            dueDetails={dueDetails}
            setDueDetails={setDueDetails}
            studentDues={studentDues}
            searchedStudent={searchedStudent}
            dueLoading={dueLoading}
            onSearchDues={handleSearchDues}
            onCreateDue={handleCreateDue}
            onClearDue={handleClearDue}
          />
        )}

        {/* TAB 3: DEPARTMENT MANAGEMENT */}
        {activeTab === "departments" && <DepartmentManagement />}

        {/* TAB 4: ADD NEW STUDENT */}
        {activeTab === "addStudent" && (
          <AddStudentForm
            newStudent={newStudent}
            setNewStudent={setNewStudent}
            onSubmit={handleAddStudentSubmit}
            loading={loading}
            onImportSuccess={() => {
              fetchStudents({ page: 1 });
              setActionMessage({
                text: "Graduates imported successfully!",
                type: "success",
              });
            }}
          />
        )}

        {/* TAB 5: SYSTEM & DATABASE SETTINGS */}
        {activeTab === "settings" && (
          <SystemSettings
            onSystemDataReset={() => {
              fetchStudents({ page: 1 });
            }}
          />
        )}

        {/* TAB 6: AUDIT & SYSTEM LOGS */}
        {activeTab === "logs" && <SystemLogs />}
      </main>

      {/* Student Details Modal */}
      <StudentDetailsModal
        show={showModal}
        onHide={() => setShowModal(false)}
        student={selectedStudent}
        onStudentUpdated={handleStudentUpdated}
        onRegeneratePassword={handleRegeneratePassword}
        regenLoadingId={regenLoadingId}
      />
    </div>
  );
};

export default AdminPanel;
